import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Clock, XCircle, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { setStatus("timeout"); return; }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentData(res.data);
        if (res.data.payment_status === "paid") { setStatus("success"); return; }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        attempts++;
        setTimeout(poll, 2000);
      } catch {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const icons = { success: CheckCircle, checking: Clock, timeout: Clock, error: XCircle, expired: XCircle };
  const Icon = icons[status] || Clock;

  return (
    <div data-testid="payment-success-page" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-lg px-6">
        <Icon size={56} className={`mx-auto mb-6 ${status === "success" ? "text-gold" : status === "checking" ? "text-white/40 animate-pulse" : "text-red-400"}`} />
        {status === "success" && (
          <>
            <h2 className="font-heading text-3xl text-white mb-4">Payment Confirmed</h2>
            <p className="text-white/60 font-body font-light text-sm leading-relaxed mb-2">
              Your payment of ${(paymentData?.amount_total / 100)?.toLocaleString()} {paymentData?.currency?.toUpperCase()} has been processed successfully.
            </p>
            <p className="text-white/40 font-body text-xs mb-8">A confirmation will be sent to your email shortly.</p>
          </>
        )}
        {status === "checking" && (
          <>
            <h2 className="font-heading text-3xl text-white mb-4">Processing Payment</h2>
            <p className="text-white/60 font-body font-light text-sm">Verifying your payment status...</p>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <h2 className="font-heading text-3xl text-white mb-4">Payment Issue</h2>
            <p className="text-white/60 font-body font-light text-sm mb-8">
              There was an issue with your payment. Please contact our team at aristocratsja@gmail.com for assistance.
            </p>
          </>
        )}
        <Link to="/" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">
          Return Home <ArrowRight size={16} />
        </Link>
      </motion.div>
    </div>
  );
}
